import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import useEntityUpdates from "hooks/useEntityUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  IconButton,
  Stack,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import api from "services/api";
import { OVERLAY_NAMES } from "constants/Overlays";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import CloudIcon from "@mui/icons-material/CloudRounded";

import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import { formatAmount } from "helpers/ServiceHelpers";
import { mergeOPIByLanguage } from "helpers/InvoiceHelpers";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import Dialog from "components/Dialog";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import { usePageTitle } from "hooks/usePageTitle";
import { Invoice } from "./InvoiceItem";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { NAVBAR_HEIGHT } from "constants/layout";

const COST_SPLIT_OPTIONS = [
  {
    key: "mileageCost",
    field: "MileageCost",
    label: "Mileage",
    detailsLabel: "Mileage",
  },
  {
    key: "parkingFeesTolls",
    field: "ParkingFeesTolls",
    label: "Parking Fees",
    detailsLabel: "Parking Fees",
  },
  {
    key: "paymentFees",
    field: "PaymentFees",
    label: "Payment Fees",
    detailsLabel: "Online Payment Processing Fees",
  },
  {
    key: "additionalCost",
    field: "AdditionalCost",
    label: "Additional Cost",
    detailsLabel: "Additional Cost",
  },
  {
    key: "additionalLinguistCost",
    field: "AdditionalLinguistCost",
    label: "Additional Linguist Cost",
    detailsLabel: "Additional Linguist Cost",
  },
  {
    key: "equipmentCost",
    field: "EquipmentCost",
    label: "Equipment",
    detailsLabel: "Equipment",
  },
  {
    key: "deliveryCost",
    field: "DeliveryCost",
    label: "Delivery Cost",
    detailsLabel: "Delivery Cost",
  },
  {
    key: "notarizationOrCOTCost",
    field: "NotarizationOrCOTCost",
    label: "Notarization Cost",
    detailsLabel: "Notarization Cost",
  },
  {
    key: "travelTimeCost",
    field: "TravelTimeCost",
    label: "Travel Time",
    detailsLabel: "Travel Time",
  },
];

const InvoiceProfile = () => {
  const [pageTitle, setPageTitle] = useState("Invoice");
  usePageTitle(pageTitle);

  const [tabValue, setTabValue] = React.useState(-1);

  const { currentUser } = useAuth();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const PAGE_PADDING = "1.5cm";

  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    setOverlayName,
    overlayData,
    setOverlayData,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for this invoice to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to specific invoice so socket joins the entity room for updates
    setCurrentEntity({
      entityType: "Invoice",
      entityId: id,
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Handler for invoice profile refetch
  const handleInvoiceProfileRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      // Trigger page refresh to reload invoice data
      triggerPageRefresh();
    },
    [triggerPageRefresh],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = `Invoice updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEntityUpdates({
    entityType: "Invoice",
    entityId: id,
    onUpdate: handleInvoiceProfileRefetch,
  });

  const [, setServiceHistories] = useState([]);

  const OTHER_INVOICES_PAGE_SIZE = 12;
  const HISTORY_FETCH_YEARS_PER_BATCH = 1;
  const EARLIEST_HISTORY_YEAR = 2000;

  const [otherInvoices, setOtherInvoices] = useState([]);
  const [otherInvoicesVisibleCount, setOtherInvoicesVisibleCount] = useState(
    OTHER_INVOICES_PAGE_SIZE,
  );
  const [historyOldestLoadedYear, setHistoryOldestLoadedYear] = useState(null);
  const [hasMoreHistoryYears, setHasMoreHistoryYears] = useState(true);
  const [isOtherInvoicesInitialLoading, setIsOtherInvoicesInitialLoading] =
    useState(false);
  const [isOtherInvoicesLoadingMore, setIsOtherInvoicesLoadingMore] =
    useState(false);
  const [latestInvoiceNumber, setLatestInvoiceNumber] = useState(null);

  const [invoiceData, setInvoiceData] = useState({});
  const [normalItems, setNormalItems] = useState([]);
  const [, setDetailedMileageItems] = useState([]);
  const [splitLanguagesItems, setSplitLanguagesItems] = useState([]);
  const [splitLanguagesMinimumItems, setSplitLanguagesMinimumItems] = useState(
    [],
  );
  const [mileageRates, setMileageRates] = useState([]);
  // Print options state
  const [firstPageRows, setFirstPageRows] = useState(11);
  const [secondPageRows, setSecondPageRows] = useState(11);
  const [rowSpacing, setRowSpacing] = useState(0.09);
  const invoiceRef = useRef(null);
  const invoiceScaleWrapperRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printStatus, setPrintStatus] = useState("Preparing PDF…");
  const [printProgress, setPrintProgress] = useState(0);
  const [showPrintOptions, setShowPrintOptions] = useState(false);

  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState(dayjs());
  const [isMarkPaidSubmitting, setIsMarkPaidSubmitting] = useState(false);
  const [isResolvingInvoiceNavigation, setIsResolvingInvoiceNavigation] =
    useState(false);

  // Dynamic cost splitting and detailed OPI state
  const [splitCostSelections, setSplitCostSelections] = useState({});
  const [detailedOPI, setDetailedOPI] = useState(true);
  const [splitLanguages, setSplitLanguages] = useState(true);
  const [splitLanguagesMinimum, setSplitLanguagesMinimum] = useState(false);
  const [splitSimultaneous, setSplitSimultaneous] = useState(false);
  const [showNote, setShowNote] = useState(true);

  const availableSplitCostOptions = useMemo(() => {
    const services = Array.isArray(invoiceData?.Services)
      ? invoiceData.Services
      : [];

    return COST_SPLIT_OPTIONS.map((option) => {
      const totalAmount = services.reduce((sum, service) => {
        const parsed = Number(service?.[option.field] || 0);
        return Number.isFinite(parsed) ? sum + parsed : sum;
      }, 0);

      return {
        ...option,
        totalAmount,
      };
    }).filter((option) => option.totalAmount > 0);
  }, [invoiceData?.Services]);

  useEffect(() => {
    setSplitCostSelections((prev) => {
      const next = {};
      availableSplitCostOptions.forEach((option) => {
        next[option.key] = prev[option.key] ?? true;
      });
      return next;
    });
  }, [availableSplitCostOptions]);

  const allCostsSelected =
    availableSplitCostOptions.length > 0 &&
    availableSplitCostOptions.every(
      (option) => splitCostSelections[option.key],
    );
  const anyCostSelected = availableSplitCostOptions.some(
    (option) => splitCostSelections[option.key],
  );
  const isMileageSplitSelected = Boolean(
    splitCostSelections.mileageCost || splitCostSelections.parkingFeesTolls,
  );

  const setAllCostsSelected = useCallback(
    (isSelected) => {
      setSplitCostSelections((prev) => {
        const next = { ...prev };
        availableSplitCostOptions.forEach((option) => {
          next[option.key] = isSelected;
        });
        return next;
      });
    },
    [availableSplitCostOptions],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch mileage rates, units, and invoice data in parallel
        const [
          mileageRatesResponse,
          unitsResponse,
          invoiceResponse,
          latestInvoiceNumberResponse,
        ] = await Promise.all([
          api.get("/mileageRates"),
          api.get("/unitTypes"),
          api.get(`/invoices/${id}`),
          api.get("/invoices/latestInvoiceNumber/"),
        ]);

        // Set mileage rates and units
        const rates = mileageRatesResponse.data || [];
        setMileageRates(rates);
        const unitsList = unitsResponse.data || [];

        // Then set invoice data and generate lists
        const invoice = invoiceResponse.data;
        setInvoiceData(invoice);
        setPageTitle("Invoice: " + invoice.Number);

        const invoiceServices = Array.isArray(invoice?.Services)
          ? invoice.Services
          : [];

        const serviceHistoryResponses = await Promise.all(
          invoiceServices.map((service) =>
            api
              .get(`/services/serviceHistory/${service.id}`)
              .catch(() => ({ data: [] })),
          ),
        );

        const invoiceServiceHistories = serviceHistoryResponses.flatMap(
          (response) => (Array.isArray(response?.data) ? response.data : []),
        );

        setServiceHistories(invoiceServiceHistories);

        if (latestInvoiceNumberResponse?.data?.latestNumber !== undefined) {
          setLatestInvoiceNumber(latestInvoiceNumberResponse.data.latestNumber);
        }

        // Generate both item lists with the mileage rates now available
        // We need to pass the rates to generateItemLists since state might not be updated yet
        const normal = [];
        const detailed = [];

        const latestHistoryByServiceId = new Map();
        (invoiceServiceHistories || []).forEach((history) => {
          const serviceId = Number(history?.ServiceID);
          if (!serviceId) return;
          const current = latestHistoryByServiceId.get(serviceId);
          if (!current) {
            latestHistoryByServiceId.set(serviceId, history);
            return;
          }
          const currentChanged = dayjs(current?.ChangedOn).valueOf() || 0;
          const nextChanged = dayjs(history?.ChangedOn).valueOf() || 0;
          if (nextChanged >= currentChanged) {
            latestHistoryByServiceId.set(serviceId, history);
          }
        });

        const getServiceFieldValue = (service, fieldName) => {
          const direct = service?.[fieldName];
          if (direct !== undefined && direct !== null && direct !== "") {
            return direct;
          }

          const latestHistory = latestHistoryByServiceId.get(
            Number(service?.id),
          );
          if (!latestHistory) return null;

          const toValue = latestHistory?.[`${fieldName}_TO`];
          if (toValue !== undefined && toValue !== null && toValue !== "") {
            return toValue;
          }

          const fromValue = latestHistory?.[`${fieldName}_FROM`];
          if (
            fromValue !== undefined &&
            fromValue !== null &&
            fromValue !== ""
          ) {
            return fromValue;
          }

          return null;
        };

        const parseDurationToDecimal = (durationString) => {
          if (!durationString) return 0;

          if (
            typeof durationString === "object" &&
            typeof durationString.hour === "function" &&
            typeof durationString.minute === "function"
          ) {
            return durationString.hour() + durationString.minute() / 60;
          }

          if (typeof durationString !== "string") return 0;
          const parts = durationString.split(":");
          if (parts.length !== 3) return 0;
          const hours = parseInt(parts[0], 10) || 0;
          const minutes = parseInt(parts[1], 10) || 0;
          const seconds = parseInt(parts[2], 10) || 0;
          return hours + minutes / 60 + seconds / 3600;
        };

        const formatHoursUnit = (decimalHours) => {
          const rounded = Math.round(decimalHours * 100) / 100;
          const formatted =
            rounded % 1 === 0
              ? String(Math.floor(rounded))
              : rounded.toFixed(2);
          const unit = parseFloat(formatted) === 1 ? "hour" : "hours";
          return `${formatted} ${unit}`;
        };

        const applyMinimumDetailsTag = (
          details,
          serviceCategory,
          totalValue,
        ) => {
          if (serviceCategory !== "Translation") return details;
          const numericTotal = parseFloat(totalValue);
          if (Number.isNaN(numericTotal)) return details;
          const isMinimumTotal = Math.abs(numericTotal - 49) < 0.005;
          if (!isMinimumTotal) return details;
          if (typeof details !== "string") return details;
          if (/\bMinimum\b/i.test(details)) return details;
          return `${details} - Minimum`;
        };

        const createSimultaneousBreakdownItems = (
          service,
          baseItem,
          targetTotal,
          paymentFees,
        ) => {
          if (!splitSimultaneous) return null;
          const serviceCategory =
            getServiceFieldValue(service, "ServiceCategory") ||
            service?.ServiceCategory;
          if (serviceCategory !== "Interpretation") {
            return null;
          }

          const modeValue =
            getServiceFieldValue(service, "Mode") || service?.Mode;
          const interpretationTypeValue =
            getServiceFieldValue(service, "InterpretationType") ||
            service?.ServiceSubtype?.Name;
          const hasSimultaneousInType =
            typeof interpretationTypeValue === "string" &&
            /simultaneous/i.test(interpretationTypeValue);

          const simultaneousRateCandidate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRateSimultaneous") ??
              getServiceFieldValue(service, "RateSimultaneous") ??
              service.ServiceClientRateSimultaneous ??
              service.RateSimultaneous ??
              service.ClientRateSimultaneous?.Rate ??
              0,
          );
          const isSimultaneousAssignment =
            modeValue === "Simultaneous" ||
            simultaneousRateCandidate > 0 ||
            hasSimultaneousInType;
          if (!isSimultaneousAssignment) {
            return null;
          }

          const prepDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "PreparationTime") ??
              service.PreparationTime ??
              service.PreparationDuration,
          );
          const schedDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "SchedulingTime") ??
              service.SchedulingTime ??
              service.SchedulingDuration,
          );
          const prepSchedDecimal = prepDecimal + schedDecimal;
          if (prepSchedDecimal <= 0) {
            return null;
          }

          const totalDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "TotalDuration") ??
              service.TotalDuration ??
              service.Duration,
          );
          if (totalDecimal <= 0) {
            return null;
          }

          const roundedTotalDecimal = Math.ceil(totalDecimal * 4) / 4;
          const coreDecimal = Math.max(
            roundedTotalDecimal - prepSchedDecimal,
            0,
          );
          if (coreDecimal <= 0) return null;

          const normalRate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRate") ??
              service.ServiceClientRate ??
              service.ClientRate?.Rate ??
              getServiceFieldValue(service, "Rate") ??
              service.Rate ??
              0,
          );
          const simultaneousRate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRateSimultaneous") ??
              getServiceFieldValue(service, "RateSimultaneous") ??
              service.ServiceClientRateSimultaneous ??
              service.RateSimultaneous ??
              service.ClientRateSimultaneous?.Rate ??
              normalRate,
          );

          if (normalRate <= 0) {
            return null;
          }

          const effectiveSimultaneousRate =
            simultaneousRate > 0 ? simultaneousRate : normalRate;

          const coreRawCharge = effectiveSimultaneousRate * coreDecimal;
          const prepSchedRawCharge = normalRate * prepSchedDecimal;
          const rawTotal = coreRawCharge + prepSchedRawCharge;
          if (rawTotal <= 0) {
            return null;
          }

          const safeTargetTotal = parseFloat(targetTotal) || 0;
          if (safeTargetTotal <= 0) {
            return null;
          }

          const scale = safeTargetTotal / rawTotal;
          const coreTotal = coreRawCharge * scale;
          const prepSchedTotal = prepSchedRawCharge * scale;

          const coreItem = {
            ...baseItem,
            details: `${service.Details} - Simultaneous Time`,
            unit: formatHoursUnit(coreDecimal),
            rate: formatAmount(effectiveSimultaneousRate),
            total: formatAmount(coreTotal.toFixed(2)),
            paymentFees,
          };

          const prepSchedItem = {
            ...baseItem,
            details: `${service.Details} - Preparation/Scheduling Time`,
            unit: formatHoursUnit(prepSchedDecimal),
            rate: formatAmount(normalRate),
            total: formatAmount(prepSchedTotal.toFixed(2)),
            paymentFees,
          };

          return [coreItem, prepSchedItem];
        };

        invoice.Services?.forEach((service) => {
          const isInterpretation = service.ServiceCategory === "Interpretation";
          const isSpeakingEngagement = /speaking\s+engagement/i.test(
            String(service.Details || ""),
          );
          const isOPIWithNoDuration =
            service.TotalDuration === "00:00:00" &&
            service.ServiceSubtype?.Name === "OPI";
          const hasUnitCount = service.UnitCount && service.UnitCount > 0;
          const hasMileageCost =
            service.MileageCost && parseFloat(service.MileageCost) > 0;
          const parkingFeesTolls = parseFloat(
            getServiceFieldValue(service, "ParkingFeesTolls") ??
              service.ParkingFeesTolls ??
              0,
          );
          const hasParkingFeesTolls = parkingFeesTolls > 0;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;

          // Get unit type name by UnitTypeID
          const getUnitTypeName = (unitTypeId) => {
            const unitType = unitsList.find((u) => u.id === unitTypeId);
            return unitType ? unitType.Name : "N/A";
          };

          // Pluralize unit name if count > 1
          const pluralizeUnit = (name, count) => {
            return count > 1 ? name + "s" : name;
          };

          const baseItem = {
            serviceId: service.id,
            serviceCategory: service.ServiceCategory,
            projectNumber: service.ProjectNumber,
            date: dayjs(service.Date).format("MM-DD-YYYY"),
            details: applyMinimumDetailsTag(
              service.Details,
              service.ServiceCategory,
              accountCharge,
            ),
            unit: isSpeakingEngagement
              ? "N/A"
              : isInterpretation
                ? isOPIWithNoDuration
                  ? "N/A"
                  : convertDurationToDecimal(service.TotalDuration)
                : hasUnitCount
                  ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                      maximumFractionDigits: 0,
                    })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                  : "N/A",
            rate: isSpeakingEngagement
              ? "N/A"
              : isOPIWithNoDuration
                ? "N/A"
                : isInterpretation
                  ? (() => {
                      const rate =
                        service.ServiceClientRate !== null &&
                        service.ServiceClientRate !== undefined
                          ? service.ServiceClientRate
                          : service.ClientRate?.Rate;
                      if (rate === 0) return "N/A";
                      return rate !== null && rate !== undefined
                        ? formatAmount(rate)
                        : "N/A";
                    })()
                  : !hasUnitCount
                    ? "N/A"
                    : (() => {
                        const rate = service.Rate;
                        if (rate === 0) return "N/A";
                        return rate !== null && rate !== undefined
                          ? formatAmount(rate)
                          : "N/A";
                      })(),
            total: formatAmount(accountCharge),
            accountCharge: accountCharge,
            paymentFees: paymentFees,
            isMileage: false,
            isFee: false,
          };

          const normalBreakdownItems = createSimultaneousBreakdownItems(
            service,
            baseItem,
            accountCharge,
            paymentFees,
          );

          if (normalBreakdownItems) {
            normal.push(...normalBreakdownItems);
          } else {
            normal.push(baseItem);
          }

          if (isInterpretation && (hasMileageCost || hasParkingFeesTolls)) {
            const chargeWithoutTravel =
              accountCharge -
              parseFloat(service.MileageCost || 0) -
              parkingFeesTolls;
            const chargeWithoutTravelFormatted = formatAmount(
              chargeWithoutTravel.toFixed(2),
            );

            const detailedMainBaseItem = {
              ...baseItem,
              total: chargeWithoutTravelFormatted,
              accountCharge: accountCharge,
              paymentFees: paymentFees,
              isMileage: false,
              isFee: false,
            };

            const detailedBreakdownItems = createSimultaneousBreakdownItems(
              service,
              detailedMainBaseItem,
              chargeWithoutTravel,
              paymentFees,
            );

            if (detailedBreakdownItems) {
              detailed.push(...detailedBreakdownItems);
            } else {
              detailed.push(detailedMainBaseItem);
            }

            const mileageRateValue = getMileageRateForDate(
              rates,
              dayjs(service.Date).toDate(),
              0.7,
            );
            const mileageRateAmount = mileageRateValue
              ? formatAmount(
                  mileageRateValue,
                  Math.round(mileageRateValue * 1000) % 10 === 0 ? 2 : 3,
                )
              : "$0.00";

            detailed.push({
              serviceId: service.id,
              serviceCategory: service.ServiceCategory,
              projectNumber: service.ProjectNumber,
              date: dayjs(service.Date).format("MM-DD-YYYY"),
              details: service.Details + " - Mileage",
              unit: (service.MileageRoundtrip || 0) + " miles",
              rate: mileageRateAmount,
              total: formatAmount(service.MileageCost),
              accountCharge: accountCharge,
              paymentFees: 0,
              isMileage: true,
              isFee: false,
            });

            if (hasParkingFeesTolls) {
              detailed.push({
                serviceId: service.id,
                serviceCategory: service.ServiceCategory,
                projectNumber: service.ProjectNumber,
                date: dayjs(service.Date).format("MM-DD-YYYY"),
                details: service.Details + " - Parking Fees",
                unit: "N/A",
                rate: "N/A",
                total: formatAmount(parkingFeesTolls),
                accountCharge: accountCharge,
                paymentFees: 0,
                isMileage: true,
                isFee: false,
              });
            }
          } else {
            const detailedBreakdownItems = createSimultaneousBreakdownItems(
              service,
              baseItem,
              accountCharge,
              paymentFees,
            );

            if (detailedBreakdownItems) {
              detailed.push(...detailedBreakdownItems);
            } else {
              detailed.push(baseItem);
            }
          }
        });

        setNormalItems(normal);
        setDetailedMileageItems(detailed);

        const getEffectiveWordCount = (service) => {
          const numericWordCount = parseFloat(service?.WordCount);
          if (!Number.isNaN(numericWordCount) && numericWordCount > 0) {
            return numericWordCount;
          }

          const detailsWordCountMatch = service?.Details?.match(
            /-\s*([\d,]+)\s+words\b/i,
          );
          if (!detailsWordCountMatch) return null;

          const parsedFromDetails = parseFloat(
            String(detailsWordCountMatch[1]).replace(/,/g, ""),
          );
          return Number.isNaN(parsedFromDetails) ? null : parsedFromDetails;
        };

        const parseSplitLanguageDetails = (details) => {
          if (typeof details !== "string") return null;

          const detailsMatch = details.match(
            /^(.+)\s+-\s+([\d,]+)\s+(.+?)\s+-\s+(.+?)\s+to\s+(.+?)\s+(Translation|Interpretation|Document Processing|Other Service)(.*)$/,
          );

          if (!detailsMatch) return null;

          return {
            prefix: detailsMatch[1],
            quantityText: `${detailsMatch[2]} ${detailsMatch[3]}`,
            quantityUnit: detailsMatch[3],
            sourceLang: detailsMatch[4],
            targetLangsText: detailsMatch[5],
            serviceType: detailsMatch[6],
            suffix: detailsMatch[7] || "",
          };
        };

        const isMinimumChargeTranslation = (service, totalCharge) => {
          if (service?.ServiceCategory !== "Translation") return false;

          const numericTotal = parseFloat(totalCharge);
          if (Number.isNaN(numericTotal)) return false;

          return Math.abs(numericTotal - 49) < 0.005;
        };

        // Generate split languages items (for WordCount > 245)
        const splitLangs = [];
        invoice.Services?.forEach((service) => {
          const serviceTargetLanguages = service.ServiceTargetLanguages || [];
          const hasMultipleLanguages = serviceTargetLanguages.length > 1;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;
          const effectiveWordCount = getEffectiveWordCount(service);
          const hasNonWordUnitCount =
            effectiveWordCount === null &&
            Number.parseFloat(service?.UnitCount) > 0;

          if (
            hasMultipleLanguages &&
            ((effectiveWordCount !== null && effectiveWordCount > 245) ||
              hasNonWordUnitCount)
          ) {
            const parsedDetails = parseSplitLanguageDetails(service.Details);

            if (!parsedDetails) {
              console.warn("[SplitLanguages] details regex did not match", {
                invoiceId: invoice?.id,
                invoiceNumber: invoice?.Number,
                serviceId: service?.id,
                details: service?.Details,
              });
            }

            if (parsedDetails) {
              const {
                prefix,
                sourceLang,
                targetLangsText,
                serviceType,
                suffix,
              } = parsedDetails;
              const normalizedQuantityUnit =
                parsedDetails.quantityUnit?.trim().toLowerCase() || "";
              const quantityText =
                normalizedQuantityUnit === "word" ||
                normalizedQuantityUnit === "words"
                  ? `${Math.round(effectiveWordCount || 0).toLocaleString(
                      "en-US",
                      {
                        maximumFractionDigits: 0,
                      },
                    )} words`
                  : parsedDetails.quantityText;

              // Split target languages (handle "Lang1, Lang2, and Lang3" format)
              const targetLanguages = targetLangsText
                .split(/,\s*and\s+|,\s+|\s+and\s+/)
                .map((lang) => lang.trim())
                .filter((lang) => lang.length > 0);

              if (targetLanguages.length > 1) {
                // Calculate charges per language (split total only, not word count)
                const totalCharge = accountCharge;
                const chargePerLanguage = totalCharge / targetLanguages.length;

                // Create an item for each target language
                targetLanguages.forEach((targetLang) => {
                  const hasUnitCount =
                    service.UnitCount && service.UnitCount > 0;
                  const getUnitTypeName = (unitTypeId) => {
                    const unitType = unitsList.find((u) => u.id === unitTypeId);
                    return unitType ? unitType.Name : "N/A";
                  };
                  const pluralizeUnit = (name, count) => {
                    return count > 1 ? name + "s" : name;
                  };
                  const unitDisplay = hasUnitCount
                    ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                      })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                    : quantityText;

                  splitLangs.push({
                    serviceId: service.id,
                    serviceCategory: service.ServiceCategory,
                    projectNumber: service.ProjectNumber,
                    date: dayjs(service.Date).format("MM-DD-YYYY"),
                    details: applyMinimumDetailsTag(
                      `${prefix} - ${quantityText} - ${sourceLang} to ${targetLang} ${serviceType}${suffix}`,
                      service.ServiceCategory,
                      chargePerLanguage,
                    ),
                    unit: unitDisplay,
                    rate: !hasUnitCount
                      ? "N/A"
                      : (() => {
                          const rate = service.Rate;
                          if (rate === 0) return "N/A";
                          return rate !== null && rate !== undefined
                            ? formatAmount(rate)
                            : "N/A";
                        })(),
                    total: formatAmount(chargePerLanguage.toFixed(2)),
                    accountCharge: accountCharge,
                    paymentFees: paymentFees,
                    isMileage: false,
                    isFee: false,
                  });
                });
              } else {
                // Single language or couldn't parse - use normal item
                splitLangs.push(
                  ...normal.filter((item) => item.serviceId === service.id),
                );
              }
            } else {
              // Couldn't parse details - use normal item
              splitLangs.push(
                ...normal.filter((item) => item.serviceId === service.id),
              );
            }
          } else {
            // Not a multi-language service - use normal item
            splitLangs.push(
              ...normal.filter((item) => item.serviceId === service.id),
            );
          }
        });

        setSplitLanguagesItems(splitLangs);

        // Generate split languages minimum items (for WordCount <= 245)
        const splitLangsMinimum = [];
        invoice.Services?.forEach((service) => {
          const serviceTargetLanguages = service.ServiceTargetLanguages || [];
          const hasMultipleLanguages = serviceTargetLanguages.length > 1;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;
          const effectiveWordCount = getEffectiveWordCount(service);
          const hasNonWordUnitCount =
            effectiveWordCount === null &&
            Number.parseFloat(service?.UnitCount) > 0;
          const isMinimumTranslation = isMinimumChargeTranslation(
            service,
            accountCharge,
          );

          if (
            hasMultipleLanguages &&
            ((effectiveWordCount !== null && effectiveWordCount <= 245) ||
              hasNonWordUnitCount) &&
            !isMinimumTranslation
          ) {
            const parsedDetails = parseSplitLanguageDetails(service.Details);

            if (!parsedDetails) {
              console.warn(
                "[SplitLanguagesMinimum] details regex did not match",
                {
                  invoiceId: invoice?.id,
                  invoiceNumber: invoice?.Number,
                  serviceId: service?.id,
                  details: service?.Details,
                },
              );
            }

            if (parsedDetails) {
              const {
                prefix,
                sourceLang,
                targetLangsText,
                serviceType,
                suffix,
              } = parsedDetails;
              const normalizedQuantityUnit =
                parsedDetails.quantityUnit?.trim().toLowerCase() || "";
              const quantityText =
                normalizedQuantityUnit === "word" ||
                normalizedQuantityUnit === "words"
                  ? `${Math.round(effectiveWordCount || 0).toLocaleString(
                      "en-US",
                      {
                        maximumFractionDigits: 0,
                      },
                    )} words`
                  : parsedDetails.quantityText;

              // Split target languages (handle "Lang1, Lang2, and Lang3" format)
              const targetLanguages = targetLangsText
                .split(/,\s*and\s+|,\s+|\s+and\s+/)
                .map((lang) => lang.trim())
                .filter((lang) => lang.length > 0);

              if (targetLanguages.length > 1) {
                // Calculate charges per language (split total only, not word count)
                const totalCharge = accountCharge;
                const chargePerLanguage = totalCharge / targetLanguages.length;

                // Create an item for each target language
                targetLanguages.forEach((targetLang) => {
                  const hasUnitCount =
                    service.UnitCount && service.UnitCount > 0;
                  const getUnitTypeName = (unitTypeId) => {
                    const unitType = unitsList.find((u) => u.id === unitTypeId);
                    return unitType ? unitType.Name : "N/A";
                  };
                  const pluralizeUnit = (name, count) => {
                    return count > 1 ? name + "s" : name;
                  };
                  const unitDisplay = hasUnitCount
                    ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                      })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                    : quantityText;

                  splitLangsMinimum.push({
                    serviceId: service.id,
                    serviceCategory: service.ServiceCategory,
                    projectNumber: service.ProjectNumber,
                    date: dayjs(service.Date).format("MM-DD-YYYY"),
                    details: applyMinimumDetailsTag(
                      `${prefix} - ${quantityText} - ${sourceLang} to ${targetLang} ${serviceType}${suffix}`,
                      service.ServiceCategory,
                      chargePerLanguage,
                    ),
                    unit: unitDisplay,
                    rate: !hasUnitCount
                      ? "N/A"
                      : (() => {
                          const rate = service.Rate;
                          if (rate === 0) return "N/A";
                          return rate !== null && rate !== undefined
                            ? formatAmount(rate)
                            : "N/A";
                        })(),
                    total: formatAmount(chargePerLanguage.toFixed(2)),
                    accountCharge: accountCharge,
                    paymentFees: paymentFees,
                    isMileage: false,
                    isFee: false,
                  });
                });
              } else {
                // Single language or couldn't parse - use normal item
                splitLangsMinimum.push(
                  ...normal.filter((item) => item.serviceId === service.id),
                );
              }
            } else {
              // Couldn't parse details - use normal item
              splitLangsMinimum.push(
                ...normal.filter((item) => item.serviceId === service.id),
              );
            }
          } else {
            // Not a multi-language service - use normal item
            splitLangsMinimum.push(
              ...normal.filter((item) => item.serviceId === service.id),
            );
          }
        });
        setSplitLanguagesMinimumItems(splitLangsMinimum);
      } catch (error) {
        const statusCode = error?.response?.status;

        if (statusCode === 404) {
          showSnackbar(`Invoice #${id} was not found.`, theme, "warning");

          const comingFrom =
            typeof data?.ComingFrom === "string" ? data.ComingFrom : null;
          const fallbackRoute =
            comingFrom && comingFrom !== `/invoices/${id}`
              ? comingFrom
              : "/invoices";

          navigate(fallbackRoute, { replace: true });
          return;
        }

        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [
    data?.ComingFrom,
    id,
    navigate,
    refreshPage,
    showSnackbar,
    splitSimultaneous,
    theme,
  ]);

  const mergeInvoicesById = useCallback((existing, incoming) => {
    const map = new Map();

    [...existing, ...incoming].forEach((invoice) => {
      if (!invoice?.id) return;
      map.set(invoice.id, invoice);
    });

    return Array.from(map.values()).sort((a, b) => {
      const dateA = dayjs(a.Date).valueOf();
      const dateB = dayjs(b.Date).valueOf();

      if (dateA !== dateB) return dateB - dateA;

      const numberA = parseInt(a.Number) || 0;
      const numberB = parseInt(b.Number) || 0;
      return numberB - numberA;
    });
  }, []);

  const fetchOtherInvoicesByYearRange = useCallback(
    async (minYear, maxYear, { append = false, loadingMore = false } = {}) => {
      if (!invoiceData?.ClientID) return;

      if (loadingMore) {
        setIsOtherInvoicesLoadingMore(true);
      } else {
        setIsOtherInvoicesInitialLoading(true);
      }

      try {
        const response = await api.get("/invoices/byClientHistory", {
          params: {
            clientId: invoiceData.ClientID,
            minYear,
            maxYear,
            excludeInvoiceId: id,
          },
        });

        const fetched = Array.isArray(response.data) ? response.data : [];

        setOtherInvoices((prev) => {
          const base = append ? prev : [];
          return mergeInvoicesById(base, fetched);
        });
      } catch (error) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load other invoices.";
        handleErrorDialog(message, theme);
      } finally {
        if (loadingMore) {
          setIsOtherInvoicesLoadingMore(false);
        } else {
          setIsOtherInvoicesInitialLoading(false);
        }
      }
    },
    [invoiceData?.ClientID, id, mergeInvoicesById, handleErrorDialog, theme],
  );

  useEffect(() => {
    if (!invoiceData?.ClientID) return;

    const currentYear = dayjs().year();
    const initialMinYear = currentYear - 1;

    setOtherInvoices([]);
    setOtherInvoicesVisibleCount(OTHER_INVOICES_PAGE_SIZE);
    setHistoryOldestLoadedYear(initialMinYear);
    setHasMoreHistoryYears(initialMinYear > EARLIEST_HISTORY_YEAR);

    fetchOtherInvoicesByYearRange(initialMinYear, currentYear, {
      append: false,
      loadingMore: false,
    });
  }, [
    invoiceData?.ClientID,
    id,
    fetchOtherInvoicesByYearRange,
    OTHER_INVOICES_PAGE_SIZE,
  ]);

  const loadPreviousYearsInvoices = useCallback(async () => {
    if (
      !hasMoreHistoryYears ||
      isOtherInvoicesLoadingMore ||
      historyOldestLoadedYear === null
    ) {
      return;
    }

    const nextMaxYear = historyOldestLoadedYear - 1;
    const nextMinYear = nextMaxYear - (HISTORY_FETCH_YEARS_PER_BATCH - 1);

    if (nextMaxYear < EARLIEST_HISTORY_YEAR) {
      setHasMoreHistoryYears(false);
      return;
    }

    await fetchOtherInvoicesByYearRange(nextMinYear, nextMaxYear, {
      append: true,
      loadingMore: true,
    });

    setHistoryOldestLoadedYear(nextMinYear);
    setHasMoreHistoryYears(nextMinYear > EARLIEST_HISTORY_YEAR);
  }, [
    hasMoreHistoryYears,
    isOtherInvoicesLoadingMore,
    historyOldestLoadedYear,
    fetchOtherInvoicesByYearRange,
    HISTORY_FETCH_YEARS_PER_BATCH,
  ]);

  const visibleOtherInvoices = useMemo(() => {
    return otherInvoices.slice(0, otherInvoicesVisibleCount);
  }, [otherInvoices, otherInvoicesVisibleCount]);

  const handleOtherInvoicesScroll = useCallback(
    async (event) => {
      const target = event.currentTarget;
      const isNearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (!isNearBottom) return;

      if (otherInvoicesVisibleCount < otherInvoices.length) {
        setOtherInvoicesVisibleCount((prev) =>
          Math.min(prev + OTHER_INVOICES_PAGE_SIZE, otherInvoices.length),
        );
        return;
      }

      if (hasMoreHistoryYears && !isOtherInvoicesLoadingMore) {
        await loadPreviousYearsInvoices();
      }
    },
    [
      otherInvoicesVisibleCount,
      otherInvoices.length,
      hasMoreHistoryYears,
      isOtherInvoicesLoadingMore,
      loadPreviousYearsInvoices,
      OTHER_INVOICES_PAGE_SIZE,
    ],
  );

  // Check if invoice contains OPI services
  const hasOPIServices = useMemo(() => {
    const items = normalItems;
    return items.some((item) => {
      if (item.serviceCategory !== "Interpretation" || !item.details)
        return false;
      return /Phone\s+.+?\s+Interpretation/.test(item.details);
    });
  }, [normalItems]);

  // Uncheck Detailed OPI when there are no OPI services
  useEffect(() => {
    if (!hasOPIServices) {
      setDetailedOPI(false);
    }
  }, [hasOPIServices]);

  // Compute final items list based on split costs, split languages settings, and detailedOPI.
  const displayItems = useMemo(() => {
    let baseItems = normalItems;

    const combineSplitLanguageItems = (primaryItems, minimumItems) => {
      const serviceOrder = (
        Array.isArray(invoiceData?.Services) ? invoiceData.Services : []
      )
        .map((service) => Number(service?.id))
        .filter((serviceId) => serviceId > 0);

      if (serviceOrder.length === 0) {
        return primaryItems;
      }

      const buildItemsByService = (items) => {
        const map = new Map();

        items.forEach((item) => {
          const serviceId = Number(item?.serviceId || 0);
          if (!serviceId) return;
          if (!map.has(serviceId)) map.set(serviceId, []);
          map.get(serviceId).push(item);
        });

        return map;
      };

      const primaryByService = buildItemsByService(primaryItems);
      const minimumByService = buildItemsByService(minimumItems);

      const scoreItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return 0;
        return items.filter((item) => !item?.isFee && !item?.isMileage).length;
      };

      const combined = [];
      serviceOrder.forEach((serviceId) => {
        const primary = primaryByService.get(serviceId) || [];
        const minimum = minimumByService.get(serviceId) || [];

        const primaryScore = scoreItems(primary);
        const minimumScore = scoreItems(minimum);

        if (minimumScore > primaryScore) {
          combined.push(...minimum);
          return;
        }

        if (primary.length > 0) {
          combined.push(...primary);
          return;
        }

        if (minimum.length > 0) {
          combined.push(...minimum);
        }
      });

      return combined;
    };

    // Apply split languages first if enabled
    if (splitLanguages) {
      baseItems = splitLanguagesItems;
    }

    // When both are enabled, combine per-service and keep the more detailed split variant.
    if (splitLanguages && splitLanguagesMinimum) {
      baseItems = combineSplitLanguageItems(
        splitLanguagesItems,
        splitLanguagesMinimumItems,
      );
    } else if (splitLanguagesMinimum) {
      // Apply split languages minimum if enabled by itself
      baseItems = splitLanguagesMinimumItems;
    }

    const itemsWithSplitCosts = (() => {
      const selectedCostOptions = availableSplitCostOptions.filter(
        (option) => splitCostSelections[option.key],
      );
      if (selectedCostOptions.length === 0) {
        return baseItems;
      }

      const parseCurrency = (value) => {
        if (value === null || value === undefined) return 0;
        const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
        return Number.isNaN(parsed) ? 0 : parsed;
      };

      const serviceById = new Map(
        (Array.isArray(invoiceData?.Services) ? invoiceData.Services : [])
          .filter((service) => Number(service?.id) > 0)
          .map((service) => [Number(service.id), service]),
      );

      const selectedCostsByService = new Map();
      serviceById.forEach((service, serviceId) => {
        const selectedCosts = selectedCostOptions
          .map((option) => ({
            ...option,
            amount: Number(service?.[option.field] || 0),
          }))
          .filter((cost) => Number.isFinite(cost.amount) && cost.amount > 0);

        if (selectedCosts.length > 0) {
          selectedCostsByService.set(serviceId, selectedCosts);
        }
      });

      const lastIndexByService = new Map();
      const chargeItemCountByService = new Map();
      const representativeByService = new Map();

      baseItems.forEach((item, index) => {
        if (!item?.serviceId) return;
        lastIndexByService.set(item.serviceId, index);

        if (
          !item.isMileage &&
          !item.isFee &&
          !representativeByService.has(item.serviceId)
        ) {
          representativeByService.set(item.serviceId, item);
        }

        if (!item.isFee && !item.isMileage) {
          const count = chargeItemCountByService.get(item.serviceId) || 0;
          chargeItemCountByService.set(item.serviceId, count + 1);
        }
      });

      const updated = [];

      baseItems.forEach((item, index) => {
        const serviceId = Number(item?.serviceId || 0);
        const selectedCosts = serviceId
          ? selectedCostsByService.get(serviceId) || []
          : [];
        const splitTotal = selectedCosts.reduce(
          (sum, cost) => sum + Number(cost.amount || 0),
          0,
        );
        const count = serviceId
          ? chargeItemCountByService.get(serviceId) || 0
          : 0;
        let nextItem = item;

        if (
          splitTotal > 0 &&
          count > 0 &&
          !item.isFee &&
          !item.isMileage &&
          item.total &&
          item.total !== "N/A"
        ) {
          const totalValue = parseCurrency(item.total);
          const adjustedTotal = Math.max(totalValue - splitTotal / count, 0);
          nextItem = {
            ...item,
            total: formatAmount(adjustedTotal.toFixed(2)),
          };
        }

        updated.push(nextItem);

        const isLastForService =
          serviceId && lastIndexByService.get(serviceId) === index;

        if (isLastForService && selectedCosts.length > 0) {
          const representative = representativeByService.get(serviceId) || item;
          const baseDetails = representative?.details || "";
          const sourceService = serviceById.get(serviceId) || {};

          const buildCostDetails = (detailsLabel) =>
            baseDetails ? `${baseDetails} - ${detailsLabel}` : detailsLabel;

          const getMileageRateAmount = () => {
            const mileageRateValue = getMileageRateForDate(
              mileageRates,
              dayjs(sourceService.Date).toDate(),
              0.7,
            );
            return mileageRateValue
              ? formatAmount(
                  mileageRateValue,
                  Math.round(mileageRateValue * 1000) % 10 === 0 ? 2 : 3,
                )
              : "N/A";
          };

          selectedCosts.forEach((cost) => {
            const isMileageCost = cost.key === "mileageCost";
            const isTravelTimeCost = cost.key === "travelTimeCost";
            const clientRate = Number(
              sourceService.ServiceClientRate ??
                sourceService.ClientRate?.Rate ??
                0,
            );
            const unit = isMileageCost
              ? `${sourceService.MileageRoundtrip || 0} miles`
              : isTravelTimeCost
                ? (() => {
                    const rawDuration = sourceService.TravelTimeDuration;
                    if (typeof rawDuration !== "string" || !rawDuration) {
                      return "N/A";
                    }
                    const parts = rawDuration.split(":");
                    return parts.length >= 2
                      ? `${parts[0]}:${parts[1]}`
                      : rawDuration;
                  })()
                : "N/A";
            const rate = isMileageCost
              ? getMileageRateAmount()
              : isTravelTimeCost && clientRate > 0
                ? formatAmount((clientRate / 2).toFixed(2))
                : "N/A";

            updated.push({
              serviceId: representative?.serviceId || serviceId,
              serviceCategory:
                representative?.serviceCategory || item?.serviceCategory,
              projectNumber:
                representative?.projectNumber || item?.projectNumber,
              date: representative?.date || item?.date,
              details: buildCostDetails(cost.detailsLabel),
              unit,
              rate,
              total: formatAmount(cost.amount.toFixed(2)),
              accountCharge: 0,
              paymentFees: cost.key === "paymentFees" ? cost.amount : 0,
              isMileage: isMileageCost,
              isFee: cost.key === "paymentFees",
            });
          });
        }
      });

      return updated;
    })();

    // Finally apply OPI merging if enabled
    if (detailedOPI) {
      return itemsWithSplitCosts;
    }

    return mergeOPIByLanguage(itemsWithSplitCosts);
  }, [
    splitLanguages,
    splitLanguagesMinimum,
    splitLanguagesItems,
    splitLanguagesMinimumItems,
    normalItems,
    detailedOPI,
    availableSplitCostOptions,
    splitCostSelections,
    invoiceData?.Services,
    mileageRates,
  ]);

  const invoiceTerms = useMemo(() => {
    const baseTerms =
      "In the event of late payment, a 10% late fee will be added after 30 business days of receiving the invoice.";
    const hasPaymentLink =
      typeof invoiceData?.PaymentLink === "string" &&
      invoiceData.PaymentLink.trim().length > 0;

    if (!hasPaymentLink) return baseTerms;

    return `${baseTerms}\nA 5% administrative charge applies to online payments only. Cash, check, and ACH are excluded.`;
  }, [invoiceData?.PaymentLink]);

  const clientHasCreditCardPaymentMethod = useMemo(() => {
    const methods = invoiceData?.Client?.ClientPaymentMethods;
    if (!Array.isArray(methods) || methods.length === 0) return false;

    return methods.some((clientPaymentMethod) => {
      const methodName = clientPaymentMethod?.PaymentMethod?.Name;
      return (
        typeof methodName === "string" &&
        methodName.trim().toLowerCase() === "credit card"
      );
    });
  }, [invoiceData?.Client?.ClientPaymentMethods]);

  const invoiceHasPaymentLink = useMemo(
    () =>
      typeof invoiceData?.PaymentLink === "string" &&
      invoiceData.PaymentLink.trim().length > 0,
    [invoiceData?.PaymentLink],
  );

  const isPrintBlockedByMissingPaymentLink =
    clientHasCreditCardPaymentMethod && !invoiceHasPaymentLink;

  const invoiceClientName =
    invoiceData.ClientName || invoiceData.Client?.Account || "N/A";
  const invoiceClientId =
    invoiceData.ClientID || invoiceData.Client?.id || null;
  const structuredAddressLine1 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine1 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();
  const structuredAddressLine2 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine2 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();
  const structuredAddressLine3 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine3 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();

  // Calculate discount values from invoice data
  const getDiscountValues = () => {
    let subtotal = parseFloat(invoiceData.Amount) || 0;
    let discount = 0;
    let total = subtotal;

    if (
      invoiceData.DiscountType === "PERCENTAGE" &&
      invoiceData.DiscountPercentage
    ) {
      const percentage = parseFloat(invoiceData.DiscountPercentage);
      discount = (subtotal * percentage) / 100;
      total = Math.max(0, subtotal - discount);
    } else if (invoiceData.DiscountAmount) {
      const amount = Math.max(0, parseFloat(invoiceData.DiscountAmount));
      discount = amount;
      total = Math.max(0, subtotal - amount);
    }

    return {
      subtotal: formatAmount(subtotal),
      discount: formatAmount(discount.toFixed(2)),
      total: formatAmount(total.toFixed(2)),
    };
  };

  const discountValues = getDiscountValues();

  const currentInvoiceNumberDisplay = invoiceData?.Number || "N/A";
  const latestInvoiceNumberDisplay = latestInvoiceNumber ?? "N/A";
  const currentInvoiceNumberNumeric = Number.parseInt(invoiceData?.Number, 10);
  const latestInvoiceNumberNumeric = Number.parseInt(latestInvoiceNumber, 10);

  const hasCurrentInvoiceNumber = Number.isFinite(currentInvoiceNumberNumeric);
  const hasLatestInvoiceNumber = Number.isFinite(latestInvoiceNumberNumeric);
  const isAtFirstInvoiceNumber =
    hasCurrentInvoiceNumber && currentInvoiceNumberNumeric <= 1;
  const isAtLatestInvoiceNumber =
    hasCurrentInvoiceNumber &&
    hasLatestInvoiceNumber &&
    currentInvoiceNumberNumeric >= latestInvoiceNumberNumeric;

  const goToAdjacentInvoiceByNumber = useCallback(
    async (direction) => {
      if (isResolvingInvoiceNavigation) return;

      if (!hasCurrentInvoiceNumber) {
        showSnackbar("Current invoice number is not valid.", theme, "warning");
        return;
      }

      if (direction !== "next" && direction !== "previous") return;
      setIsResolvingInvoiceNavigation(true);

      try {
        const response = await api.get("/invoices/adjacentByNumber", {
          params: {
            number: currentInvoiceNumberNumeric,
            direction,
          },
        });

        const targetInvoiceId = response?.data?.invoice?.id;
        if (!targetInvoiceId) {
          showSnackbar(
            direction === "next"
              ? "No next invoice was found."
              : "No previous invoice was found.",
            theme,
            "warning",
          );
          return;
        }

        navigate(`/invoices/${targetInvoiceId}`, {
          state: { ComingFrom: `/invoices/${id}` },
        });
      } catch (error) {
        if (error?.response?.status === 404) {
          showSnackbar(
            direction === "next"
              ? "No next invoice was found."
              : "No previous invoice was found.",
            theme,
            "warning",
          );
          return;
        }

        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to navigate to invoice.";
        handleErrorDialog(message, theme);
      } finally {
        setIsResolvingInvoiceNavigation(false);
      }
    },
    [
      handleErrorDialog,
      hasCurrentInvoiceNumber,
      id,
      currentInvoiceNumberNumeric,
      isResolvingInvoiceNavigation,
      navigate,
      showSnackbar,
      theme,
    ],
  );

  const goToClientProfile = useCallback(() => {
    if (!invoiceClientId) return;

    navigate(`/clients/${invoiceClientId}`, {
      state: { ComingFrom: `/invoices/${id}` },
    });
  }, [invoiceClientId, navigate, id]);

  const openMarkPaidDialog = () => {
    setMarkPaidDate(dayjs());
    setMarkPaidDialogOpen(true);
  };

  const closeMarkPaidDialog = () => {
    if (isMarkPaidSubmitting) return;
    setMarkPaidDialogOpen(false);
  };

  const markServicesPaidForInvoice = async (services = []) => {
    if (!services.length) return;

    await Promise.all(
      services.map((service) =>
        api.post(`/services/markServicePaid`, {
          data: {
            id: service.id,
            Paid: true,
          },
        }),
      ),
    );
  };

  const handleConfirmMarkPaid = async () => {
    if (!invoiceData?.id) return;

    const paymentDate = (markPaidDate || dayjs()).format("YYYY-MM-DD");

    setIsMarkPaidSubmitting(true);

    try {
      await api.put("/invoices/markPaid", {
        invoiceIds: [invoiceData.id],
        paymentDate,
      });

      await markServicesPaidForInvoice(invoiceData.Services || []);

      triggerPageRefresh("Invoice marked as paid", theme);
      closeMarkPaidDialog();
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to mark invoice as paid.";
      handleErrorDialog(message, theme);
    } finally {
      setIsMarkPaidSubmitting(false);
    }
  };

  const invoicePaymentStatus = useMemo(() => {
    if (invoiceData.PaymentReceivedDate) return "Paid";
    if (
      invoiceData.DueDate &&
      dayjs().diff(dayjs(invoiceData.DueDate), "day") > 30
    ) {
      return "Overdue";
    }
    return "Not Paid";
  }, [invoiceData.PaymentReceivedDate, invoiceData.DueDate]);

  const openOneDriveLink = () => {
    const financialRecordsBaseUrl = `https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FComm%2E%20Essentials%20Operations%2FFinancial%20Records`;

    const invoiceDate = dayjs(invoiceData.ServicesDate);
    const year = invoiceDate.format("YYYY");
    const month = invoiceDate.format("MMM");
    const monthAbbrev = month.substring(0, 3);

    // Determine which OneDrive folder to open based on when the invoice
    // was marked as paid. On the same day it's marked paid, open the
    // "Invoiced" folder so the user can move the PDF to "Paid". After
    // that day, open the "Paid" folder directly.
    const markedPaidOn = invoiceData.PaymentMarkedPaidOn
      ? dayjs(invoiceData.PaymentMarkedPaidOn)
      : null;
    const wasMarkedPaidToday =
      markedPaidOn && markedPaidOn.isSame(dayjs(), "day");

    let oneDriveLink;
    if (wasMarkedPaidToday) {
      // Same day as marking paid — open Invoiced folder to move the PDF
      if (invoiceData.Client.Account === "CWS Global") {
        oneDriveLink = `${financialRecordsBaseUrl}/CWS%20Unpaid%20Invoices`;
      } else {
        oneDriveLink = `${financialRecordsBaseUrl}/Invoiced/${year}/${monthAbbrev}`;
      }
    } else {
      // Not paid today (or not paid at all) — open Paid folder
      oneDriveLink = `${financialRecordsBaseUrl}/Paid`;
    }

    window.open(oneDriveLink, "_blank");
  };
  const deleteService = () => {
    api
      .post("/invoices/deleteInvoice", {
        invoiceId: invoiceData.id,
        changedBy: currentUser.id,
        changedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      })
      .then(() => {
        triggerPageRefresh("Invoice Deleted Sucessfully", theme);
        const rawNavBackTo =
          overlayData?.ComingFrom || data?.ComingFrom || "/invoices";
        const normalizedNavBackTo = String(rawNavBackTo).startsWith("/")
          ? String(rawNavBackTo)
          : `/${rawNavBackTo}`;

        navigate(normalizedNavBackTo);
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete invoice.";
        handleErrorDialog(message, theme);
      });
  };

  // Parse address string into display lines, preserving newline-separated format.
  const parseAddress = (addressString) => {
    const normalizeAddressPart = (value) => {
      const normalized = String(value || "").trim();
      if (!normalized || normalized.toUpperCase() === "N/A") return null;
      return normalized;
    };

    if (!addressString) {
      return { line1: "", line2: "", zipCode: "" };
    }

    const normalized = String(addressString).replace(/\r\n?/g, "\n").trim();

    // Legacy payloads may contain multiple addresses separated with pipe.
    const primaryAddress = normalized.includes(" | ")
      ? normalized.split(" | ")[0].trim()
      : normalized;

    const lines = primaryAddress
      .split("\n")
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (lines.length >= 3) {
      return {
        line1: lines[0],
        line2: lines[1],
        zipCode: lines.slice(2).join(" "),
      };
    }

    if (lines.length === 2) {
      return {
        line1: lines[0],
        line2: "",
        zipCode: lines[1],
      };
    }

    // Legacy addresses may still be stored as a single comma-delimited line.
    const commaParts = primaryAddress
      .split(",")
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (commaParts.length >= 3) {
      return {
        line1: commaParts[0],
        line2: commaParts.slice(1, -1).join(", "),
        zipCode: commaParts[commaParts.length - 1],
      };
    }

    if (commaParts.length === 2) {
      return {
        line1: commaParts[0],
        line2: "",
        zipCode: commaParts[1],
      };
    }

    return {
      line1: normalizeAddressPart(primaryAddress) || "",
      line2: "",
      zipCode: "",
    };
  };

  const getInvoiceClientAddress = () => {
    if (
      structuredAddressLine1 ||
      structuredAddressLine2 ||
      structuredAddressLine3
    ) {
      return {
        line1: structuredAddressLine1,
        line2: structuredAddressLine2,
        zipCode: structuredAddressLine3,
      };
    }

    return parseAddress(invoiceData.Address || invoiceData.Client?.MainAddress);
  };

  // Convert duration string (HH:MM:SS) to decimal hours with proper formatting
  const convertDurationToDecimal = (durationString) => {
    if (!durationString) return "0 hours";

    const formatDecimalHours = (decimalHours) => {
      const rounded = Math.round(decimalHours * 100) / 100;
      const formattedHours =
        rounded % 1 === 0 ? Math.floor(rounded) : rounded.toFixed(2);
      const unit = parseFloat(formattedHours) === 1 ? "hour" : "hours";
      return `${formattedHours} ${unit}`;
    };

    if (typeof durationString === "number" && Number.isFinite(durationString)) {
      return formatDecimalHours(durationString);
    }

    if (typeof durationString !== "string") return "0 hours";

    const normalizedDuration = durationString.trim();
    const numericDuration = parseFloat(normalizedDuration);
    if (
      normalizedDuration !== "" &&
      !normalizedDuration.includes(":") &&
      Number.isFinite(numericDuration)
    ) {
      return formatDecimalHours(numericDuration);
    }

    const parts = normalizedDuration.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseInt(parts[2]) || 0;
      const decimalHours = hours + minutes / 60 + seconds / 3600;

      return formatDecimalHours(decimalHours);
    }
    return normalizedDuration;
  };

  const handlePrintInvoiceJsPdfLinks = async () => {
    if (isPrintBlockedByMissingPaymentLink) {
      showSnackbar(
        "Payment Link is required before exporting PDF for credit card clients.",
        theme,
        "error",
      );
      return;
    }

    if (!invoiceRef.current) return;
    setIsPrinting(true);
    setPrintStatus("Preparing PDF…");
    setPrintProgress(0);

    // Store original scale and remove it temporarily for accurate PDF capture
    const originalScale = invoiceScaleWrapperRef.current?.style.transform;
    if (invoiceScaleWrapperRef.current) {
      invoiceScaleWrapperRef.current.style.transform = "scale(1)";
    }

    // Remove border radius for PDF capture
    const invoiceItems = invoiceRef.current.querySelectorAll(".InvoiceItem");
    const originalStyles = [];
    invoiceItems.forEach((item) => {
      originalStyles.push(item.style.borderRadius);
      item.style.borderRadius = "0";
    });

    try {
      const { default: html2canvas } = await import("html2canvas");
      const jsPDF = (await import("jspdf")).default;

      const pageElements = Array.from(invoiceRef.current.children);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [215.9, 279.4],
      });

      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i];
        setPrintStatus(`Rendering page ${i + 1} of ${pageElements.length}…`);
        setPrintProgress(Math.round((i / pageElements.length) * 100));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 0));

        const canvas = await html2canvas(pageElement, {
          scale: 8,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          imageSmoothingEnabled: true,
        });

        await new Promise((resolve) => requestAnimationFrame(resolve));

        const imgData = canvas.toDataURL("image/png");

        if (i > 0) {
          pdf.addPage([215.9, 279.4], "portrait");
        }

        pdf.addImage(imgData, "JPEG", 0, 0, 215.9, 279.4, undefined, "FAST");

        // Overlay link annotations for anchors (mailto, http/https, tel) on this page
        const anchors = pageElement.querySelectorAll(
          'a[href^="mailto:"], a[href^="http://"], a[href^="https://"], a[href^="tel:"]',
        );
        if (anchors.length) {
          const pageRect = pageElement.getBoundingClientRect();
          const mmPerPxX = 215.9 / pageRect.width;
          const mmPerPxY = 279.4 / pageRect.height;

          anchors.forEach((anchor) => {
            const rect = anchor.getBoundingClientRect();
            const href = anchor.getAttribute("href");
            if (!href) return;

            const xMm = (rect.left - pageRect.left) * mmPerPxX;
            const yMm = (rect.top - pageRect.top) * mmPerPxY;
            const wMm = rect.width * mmPerPxX;
            const hMm = rect.height * mmPerPxY;

            if (wMm > 0 && hMm > 0) {
              pdf.link(xMm, yMm, wMm, hMm, { url: href });
            }
          });
        }
      }

      setPrintStatus("Saving PDF…");
      setPrintProgress(100);

      const clientName =
        invoiceData.ClientName || invoiceData.Client?.Account || "Client";
      const detailedSuffix = detailedOPI && hasOPIServices ? " Detailed" : "";
      pdf.save(
        `Invoice ${invoiceData.Number} ${clientName}${detailedSuffix}.pdf`,
      );
      setPrintStatus("Done");
    } catch (err) {
      console.error("PDF print failed", err);
      setPrintStatus("Failed to generate PDF");
    } finally {
      invoiceItems.forEach((item, index) => {
        item.style.borderRadius = originalStyles[index];
      });
      // Restore original scale transform
      if (invoiceScaleWrapperRef.current && originalScale) {
        invoiceScaleWrapperRef.current.style.transform = originalScale;
      }
      setIsPrinting(false);
    }
  };

  return (
    <>
      <Box
        p="1rem 1rem 0 1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        }}
      >
        {isMobile ? (
          // Mobile: Stacked layout (Preview + Actions visible together)
          <Box
            bgcolor={theme.palette.background.alt}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {/* Collapsible Details Section */}
            <Box
              onClick={() => setTabValue(tabValue === 0 ? -1 : 0)}
              sx={{
                p: "1rem",
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Invoice Details
              </Typography>
              {tabValue === 0 ? (
                <ExpandLessRoundedIcon />
              ) : (
                <ExpandMoreRoundedIcon />
              )}
            </Box>

            {tabValue === 0 && (
              <Box
                sx={{
                  p: "1rem",
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Invoice Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceData.Number || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Client
                    </Typography>
                    <Typography
                      variant="body1"
                      onClick={goToClientProfile}
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        cursor: invoiceClientId ? "pointer" : "default",
                        textDecoration: "none",
                        "&:hover": invoiceClientId
                          ? { textDecoration: "underline" }
                          : {},
                      }}
                    >
                      {invoiceClientName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Issue Date
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.Date
                        ? dayjs(invoiceData.Date).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Due Date
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.DueDate
                        ? dayjs(invoiceData.DueDate).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {formatAmount(invoiceData.Amount)}
                    </Typography>

                    {/* Discount Display */}
                    {((discountValues.discount &&
                      discountValues.discount !== "N/A") ||
                      invoiceData.DiscountNote) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {invoiceData.DiscountType === "PERCENTAGE"
                            ? `Discount (${invoiceData.DiscountPercentage}%):`
                            : invoiceData.DiscountType === "FIXED"
                              ? "Discount Amount:"
                              : "Discount:"}
                        </Typography>
                        <Typography variant="body1">
                          {formatAmount(discountValues.discount)}
                          {invoiceData.DiscountNote && (
                            <Box sx={{ ml: 1 }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                }}
                              >
                                ({invoiceData.DiscountNote})
                              </Typography>
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Payment Status
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: (() => {
                          if (invoicePaymentStatus === "Paid")
                            return theme.palette.primary.main;
                          if (invoicePaymentStatus === "Overdue")
                            return theme.palette.error.main;
                          return theme.palette.warning[400];
                        })(),
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "13px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {invoicePaymentStatus}
                      </Typography>
                    </Box>
                  </Box>

                  {invoiceData.PaymentReceivedDate && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Paid On
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(invoiceData.PaymentReceivedDate).format(
                          "MMM DD, YYYY",
                        )}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {/* Invoice Preview */}
            <Box
              sx={{
                p: "1rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "auto",
              }}
            >
              <Box
                ref={invoiceScaleWrapperRef}
                sx={{
                  transform: "scale(0.65)",
                  transformOrigin: "top center",
                  transition: "transform 0.3s ease",
                }}
              >
                <Invoice
                  ref={invoiceRef}
                  pagePadding={PAGE_PADDING}
                  invoiceId={invoiceData.id}
                  invoiceNumber={invoiceData.Number}
                  dateOfIssue={dayjs(invoiceData.Date).format("MM-DD-YYYY")}
                  dueDate={dayjs(invoiceData.DueDate).format("MM-DD-YYYY")}
                  paidDate={
                    invoiceData.PaymentReceivedDate
                      ? dayjs(invoiceData.PaymentReceivedDate).format(
                          "MM-DD-YYYY",
                        )
                      : null
                  }
                  clientId={invoiceClientId}
                  clientName={invoiceClientName}
                  clientAddress={getInvoiceClientAddress()}
                  purchaseOrders={invoiceData.PurchaseOrder}
                  items={displayItems}
                  subtotal={discountValues.subtotal}
                  discount={discountValues.discount}
                  total={discountValues.total}
                  notes={showNote ? invoiceData.Note : null}
                  terms={invoiceTerms}
                  itemsPerFirstPage={firstPageRows}
                  itemsPerPage={secondPageRows}
                  rowSpacing={rowSpacing + "cm"}
                  splitMileage={isMileageSplitSelected}
                  paymentLink={invoiceData.PaymentLink}
                />
              </Box>
            </Box>

            <Divider />

            {/* Actions Section */}
            <Box sx={{ p: "1rem", overflowY: "auto" }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ textAlign: "center", mb: 0.5 }}
              >
                Navigation
              </Typography>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <IconButton
                  size="medium"
                  onClick={() => goToAdjacentInvoiceByNumber("previous")}
                  disabled={
                    isResolvingInvoiceNavigation || isAtFirstInvoiceNumber
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ChevronLeftRoundedIcon fontSize="medium" />
                </IconButton>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  {`${currentInvoiceNumberDisplay} / ${latestInvoiceNumberDisplay}`}
                </Typography>
                <IconButton
                  size="medium"
                  onClick={() => goToAdjacentInvoiceByNumber("next")}
                  disabled={
                    isResolvingInvoiceNavigation || isAtLatestInvoiceNumber
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ChevronRightRoundedIcon fontSize="medium" />
                </IconButton>
              </Stack>

              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Invoice Actions
              </Typography>

              <Box width="100%" sx={{ mt: 2 }}>
                {invoiceData.PaymentReceivedDate ? null : (
                  <ListItem key="MarkAsPaid" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        openMarkPaidDialog();
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <CreditScoreRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Mark As Paid" />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem key="EditInvoice" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => {
                      const data = {
                        Action: OVERLAY_NAMES.INVOICES_ACTION_EDIT,
                        invoiceData: invoiceData,
                      };
                      setOverlayData(data);
                      setOverlayName(OVERLAY_NAMES.INVOICES_ACTION_EDIT);
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <EditRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Edit Invoice" />
                  </ListItemButton>
                </ListItem>

                <ListItem key="PrintJsPdfLinks" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    disabled={isPrinting || isPrintBlockedByMissingPaymentLink}
                    onClick={() => {
                      handlePrintInvoiceJsPdfLinks();
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <PictureAsPdfRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Export PDF"
                      secondary={
                        isPrintBlockedByMissingPaymentLink
                          ? "Add Payment Link first"
                          : null
                      }
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem key="DeleteAssignment" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Delete Invoice",
                        message: "Do you really wish to delete this invoice?",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Delete",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          deleteService();
                          closeDialog();
                        },
                      });
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <DeleteRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Delete Invoice" />
                  </ListItemButton>
                </ListItem>

                <ListItem key="OneDrive" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => openOneDriveLink()}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <CloudIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Open One Drive Link" />
                  </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Invoices Options
                </Typography>

                <ListItem key="SplitCostsAll" disablePadding sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allCostsSelected}
                        indeterminate={anyCostSelected && !allCostsSelected}
                        onChange={(e) => setAllCostsSelected(e.target.checked)}
                        disabled={availableSplitCostOptions.length === 0}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Costs"
                    sx={{
                      color:
                        availableSplitCostOptions.length > 0
                          ? theme.palette.primary.main
                          : theme.palette.text.disabled,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                {availableSplitCostOptions.map((costOption) => (
                  <ListItem
                    key={`SplitCost-${costOption.key}`}
                    disablePadding
                    sx={{ pl: "1.6rem" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(splitCostSelections[costOption.key])}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSplitCostSelections((prev) => ({
                              ...prev,
                              [costOption.key]: checked,
                            }));
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            "&.Mui-checked": {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={costOption.label}
                      sx={{
                        color: theme.palette.primary.main,
                        width: "100%",
                        m: 0,
                        ml: "0.5rem",
                      }}
                    />
                  </ListItem>
                ))}

                <ListItem key="SplitLanguages" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitLanguages}
                        onChange={(e) => setSplitLanguages(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Languages"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="SplitLanguagesMinimum" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitLanguagesMinimum}
                        onChange={(e) =>
                          setSplitLanguagesMinimum(e.target.checked)
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Languages Minimum"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="SplitSimultaneous" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitSimultaneous}
                        onChange={(e) => setSplitSimultaneous(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Simultaneous"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="DetailedOPI" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={detailedOPI}
                        onChange={(e) => setDetailedOPI(e.target.checked)}
                        disabled={!hasOPIServices}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Detailed OPI"
                    sx={{
                      color: hasOPIServices
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="ShowNote" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showNote}
                        onChange={(e) => setShowNote(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Show Note"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.7,
                    },
                  }}
                  onClick={() => setShowPrintOptions(!showPrintOptions)}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Print Options
                  </Typography>
                  {showPrintOptions ? (
                    <ExpandLessRoundedIcon sx={{ ml: 1 }} />
                  ) : (
                    <ExpandMoreRoundedIcon sx={{ ml: 1 }} />
                  )}
                </Box>

                {showPrintOptions && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        First Page Rows:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={firstPageRows}
                        onChange={(e) =>
                          setFirstPageRows(Number(e.target.value))
                        }
                        inputProps={{ min: 1, max: 20 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        Other Page Rows:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={secondPageRows}
                        onChange={(e) =>
                          setSecondPageRows(Number(e.target.value))
                        }
                        inputProps={{ min: 1, max: 20 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        Row Spacing:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={rowSpacing}
                        onChange={(e) => setRowSpacing(Number(e.target.value))}
                        inputProps={{ step: 0.005, min: 0 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          // Desktop: 3-Column layout
          <Box
            bgcolor={theme.palette.background.alt}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              overflow: "auto",
              maxHeight: "100%",
            }}
          >
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {/* Sidebar with Invoice Details and Quick Links */}
              <Grid
                size={{ xs: 12, sm: 12, md: "auto" }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "280px",
                  maxWidth: "280px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: ".75rem 0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Invoice Details Section */}
                    <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Invoice Details
                      </Typography>

                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Invoice Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {invoiceData.Number || "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Client
                          </Typography>
                          <Typography
                            variant="body1"
                            onClick={goToClientProfile}
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              cursor: invoiceClientId ? "pointer" : "default",
                              textDecoration: "none",
                              "&:hover": invoiceClientId
                                ? { textDecoration: "underline" }
                                : {},
                            }}
                          >
                            {invoiceClientName}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Issue Date
                          </Typography>
                          <Typography variant="body1">
                            {invoiceData.Date
                              ? dayjs(invoiceData.Date).format("MMM DD, YYYY")
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Due Date
                          </Typography>
                          <Typography variant="body1">
                            {invoiceData.DueDate
                              ? dayjs(invoiceData.DueDate).format(
                                  "MMM DD, YYYY",
                                )
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Amount
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {formatAmount(invoiceData.Amount)}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Payment Status
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              backgroundColor: (() => {
                                if (invoicePaymentStatus === "Paid")
                                  return theme.palette.primary.main;
                                if (invoicePaymentStatus === "Overdue")
                                  return theme.palette.error.main;
                                return theme.palette.warning[400];
                              })(),
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "13px",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {invoicePaymentStatus}
                            </Typography>
                          </Box>
                        </Box>

                        {invoiceData.PaymentReceivedDate && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Paid On
                            </Typography>
                            <Typography variant="body1">
                              {dayjs(invoiceData.PaymentReceivedDate).format(
                                "MMM DD, YYYY",
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Quick Links Section */}
                    <Grid
                      size={{ xs: 12 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <Box
                        onScroll={handleOtherInvoicesScroll}
                        sx={{
                          mt: 0,
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          Services ({invoiceData.Services?.length || 0})
                        </Typography>

                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {invoiceData.Services?.slice(0, 15).map((service) => {
                            const servicePath =
                              service.ServiceCategory === "Interpretation"
                                ? `/interpretations/${service.id}`
                                : service.ServiceCategory === "Translation"
                                  ? `/translations/${service.id}`
                                  : `/otherServices/${service.id}`;

                            return (
                              <Box
                                key={service.id}
                                component="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(servicePath);
                                }}
                                sx={{
                                  textDecoration: "none",
                                  color: theme.palette.primary.main,
                                  cursor: "pointer",
                                  p: 1,
                                  borderRadius: 1,
                                  transition: "background-color 0.2s",
                                  width: "100%",
                                  textAlign: "left",
                                  border: "none",
                                  background: "transparent",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  #{service.ProjectNumber}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {dayjs(service.Date).format("MMM DD, YYYY")} •{" "}
                                  {service.ServiceCategory}
                                </Typography>
                              </Box>
                            );
                          })}
                          {invoiceData.Services?.length > 15 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ pl: 1, pt: 1 }}
                            >
                              + {invoiceData.Services.length - 15} more services
                            </Typography>
                          )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          Other Invoices
                        </Typography>

                        {isOtherInvoicesInitialLoading ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 1 }}
                          >
                            Loading other invoices...
                          </Typography>
                        ) : visibleOtherInvoices.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 1 }}
                          >
                            No other invoices found for this client.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {visibleOtherInvoices.map((otherInvoice) => (
                              <Box
                                key={otherInvoice.id}
                                onClick={() => {
                                  const stateData = {
                                    ComingFrom: `/invoices/${id}`,
                                  };
                                  navigate(`/invoices/${otherInvoice.id}`, {
                                    state: stateData,
                                  });
                                }}
                                sx={{
                                  textDecoration: "none",
                                  color: theme.palette.primary.main,
                                  cursor: "pointer",
                                  p: 1,
                                  borderRadius: 1,
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  #{otherInvoice.Number || "N/A"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {otherInvoice.Date
                                    ? dayjs(otherInvoice.Date).format(
                                        "MMM DD, YYYY",
                                      )
                                    : "N/A"}
                                  {" • "}
                                  {formatAmount(otherInvoice.Amount)}
                                </Typography>
                              </Box>
                            ))}

                            {isOtherInvoicesLoadingMore && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ px: 1, py: 0.5 }}
                              >
                                Loading more...
                              </Typography>
                            )}

                            {!isOtherInvoicesLoadingMore &&
                              otherInvoicesVisibleCount <
                                otherInvoices.length && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ px: 1, py: 0.5 }}
                                >
                                  Scroll for more
                                </Typography>
                              )}
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Divider
                orientation={isSmallScreen ? "horizontal" : "vertical"}
                flexItem
              />

              <Grid
                size={{ xs: 12, sm: 12, md: "grow" }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "400px",
                  maxWidth: "400px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        overflow: "auto",
                        py: ".5rem",
                        bgcolor: theme.palette.background.alt,
                      }}
                    >
                      <Invoice
                        ref={invoiceRef}
                        pagePadding={PAGE_PADDING}
                        invoiceId={invoiceData.id}
                        invoiceNumber={invoiceData.Number}
                        dateOfIssue={dayjs(invoiceData.Date).format(
                          "MM-DD-YYYY",
                        )}
                        dueDate={dayjs(invoiceData.DueDate).format(
                          "MM-DD-YYYY",
                        )}
                        paidDate={
                          invoiceData.PaymentReceivedDate
                            ? dayjs(invoiceData.PaymentReceivedDate).format(
                                "MM-DD-YYYY",
                              )
                            : null
                        }
                        clientId={invoiceClientId}
                        clientName={invoiceClientName}
                        clientAddress={getInvoiceClientAddress()}
                        purchaseOrders={invoiceData.PurchaseOrder}
                        items={displayItems}
                        subtotal={discountValues.subtotal}
                        discount={discountValues.discount}
                        total={discountValues.total}
                        notes={showNote ? invoiceData.Note : null}
                        terms={invoiceTerms}
                        itemsPerFirstPage={firstPageRows}
                        itemsPerPage={secondPageRows}
                        rowSpacing={rowSpacing + "cm"}
                        splitMileage={isMileageSplitSelected}
                        paymentLink={invoiceData.PaymentLink}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Divider
                orientation={isSmallScreen ? "horizontal" : "vertical"}
                flexItem
              />

              <Grid
                size={{ xs: 12, sm: 12, md: 3 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "240px",
                  maxWidth: "240px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    overflowY: "auto",
                    flex: 1,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: ".75rem 0",
                      overflowY: "auto",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                      Navigation
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <IconButton
                        size="medium"
                        onClick={() => goToAdjacentInvoiceByNumber("previous")}
                        disabled={
                          isResolvingInvoiceNavigation || isAtFirstInvoiceNumber
                        }
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <ChevronLeftRoundedIcon fontSize="medium" />
                      </IconButton>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, lineHeight: 1.1 }}
                      >
                        {`${currentInvoiceNumberDisplay} / ${latestInvoiceNumberDisplay}`}
                      </Typography>
                      <IconButton
                        size="medium"
                        onClick={() => goToAdjacentInvoiceByNumber("next")}
                        disabled={
                          isResolvingInvoiceNavigation ||
                          isAtLatestInvoiceNumber
                        }
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <ChevronRightRoundedIcon fontSize="medium" />
                      </IconButton>
                    </Stack>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Invoice Actions
                      </Typography>
                    </Grid>

                    <Box width="100%">
                      {invoiceData.PaymentReceivedDate ? null : (
                        <ListItem key="MarkAsPaid" disablePadding>
                          <ListItemButton
                            sx={{
                              color: theme.palette.primary.main,
                              borderRadius: "8px",
                              border: `1px solid ${theme.palette.divider}`,
                              mb: 1,
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                            onClick={() => {
                              openMarkPaidDialog();
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                mr: "-1rem",
                                color: theme.palette.primary.main,
                              }}
                            >
                              <CreditScoreRoundedIcon
                                sx={{
                                  fontSize: 28,
                                  color: theme.palette.primary.main,
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Mark As Paid" />
                          </ListItemButton>
                        </ListItem>
                      )}

                      <ListItem key="EditInvoice" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const data = {
                              Action: OVERLAY_NAMES.INVOICES_ACTION_EDIT,
                              invoiceData: invoiceData,
                            };
                            setOverlayData(data);
                            setOverlayName(OVERLAY_NAMES.INVOICES_ACTION_EDIT);
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EditRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Edit Invoice" />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="PrintJsPdfLinks" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          disabled={
                            isPrinting || isPrintBlockedByMissingPaymentLink
                          }
                          onClick={() => {
                            handlePrintInvoiceJsPdfLinks();
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <PictureAsPdfRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary="Export PDF"
                            secondary={
                              isPrintBlockedByMissingPaymentLink
                                ? "Add Payment Link first"
                                : null
                            }
                          />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="DeleteAssignment" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Delete Invoice",
                              message:
                                "Do you really wish to delete this invoice?",
                              customContent: null,
                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Delete",
                              tertiaryOnClick: null,
                              secondaryOnClick: () => {
                                closeDialog();
                              },
                              primaryOnClick: () => {
                                deleteService();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <DeleteRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Delete Invoice" />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="OneDrive" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => openOneDriveLink()}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CloudIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Open One Drive Link" />
                        </ListItemButton>
                      </ListItem>

                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Invoices Options
                      </Typography>

                      <ListItem
                        key="SplitCostsAll"
                        disablePadding
                        sx={{ mt: 1 }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allCostsSelected}
                              indeterminate={
                                anyCostSelected && !allCostsSelected
                              }
                              onChange={(e) =>
                                setAllCostsSelected(e.target.checked)
                              }
                              disabled={availableSplitCostOptions.length === 0}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Costs"
                          sx={{
                            color:
                              availableSplitCostOptions.length > 0
                                ? theme.palette.primary.main
                                : theme.palette.text.disabled,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      {availableSplitCostOptions.map((costOption) => (
                        <ListItem
                          key={`SplitCost-${costOption.key}`}
                          disablePadding
                          sx={{ pl: "1.6rem" }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(
                                  splitCostSelections[costOption.key],
                                )}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSplitCostSelections((prev) => ({
                                    ...prev,
                                    [costOption.key]: checked,
                                  }));
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  "&.Mui-checked": {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              />
                            }
                            label={costOption.label}
                            sx={{
                              color: theme.palette.primary.main,
                              width: "100%",
                              m: 0,
                              ml: "0.5rem",
                            }}
                          />
                        </ListItem>
                      ))}

                      <ListItem key="SplitLanguages" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitLanguages}
                              onChange={(e) =>
                                setSplitLanguages(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Languages"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="SplitLanguagesMinimum" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitLanguagesMinimum}
                              onChange={(e) =>
                                setSplitLanguagesMinimum(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Languages Minimum"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="SplitSimultaneous" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitSimultaneous}
                              onChange={(e) =>
                                setSplitSimultaneous(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Simultaneous"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="DetailedOPI" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={detailedOPI}
                              onChange={(e) => setDetailedOPI(e.target.checked)}
                              disabled={!hasOPIServices}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Detailed OPI"
                          sx={{
                            color: hasOPIServices
                              ? theme.palette.primary.main
                              : theme.palette.text.disabled,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="ShowNote" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showNote}
                              onChange={(e) => setShowNote(e.target.checked)}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Show Note"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>
                    </Box>

                    <Divider
                      sx={{
                        my: 2,
                      }}
                    />

                    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.7,
                          },
                        }}
                        onClick={() => setShowPrintOptions(!showPrintOptions)}
                      >
                        <Typography variant="h5" fontWeight="bold">
                          Print Options
                        </Typography>
                        {showPrintOptions ? (
                          <ExpandLessRoundedIcon sx={{ ml: 1 }} />
                        ) : (
                          <ExpandMoreRoundedIcon sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </Grid>

                    {showPrintOptions && (
                      <>
                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              First Page Rows:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={firstPageRows}
                              onChange={(e) =>
                                setFirstPageRows(Number(e.target.value))
                              }
                              inputProps={{ min: 1, max: 20 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              Other Page Rows:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={secondPageRows}
                              onChange={(e) =>
                                setSecondPageRows(Number(e.target.value))
                              }
                              inputProps={{ min: 1, max: 20 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              Row Spacing:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={rowSpacing}
                              onChange={(e) =>
                                setRowSpacing(Number(e.target.value))
                              }
                              inputProps={{ step: 0.005, min: 0 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      <Dialog
        theme={theme}
        openDialog={markPaidDialogOpen}
        title="Mark As Paid"
        customContent={
          <Stack spacing={2}>
            <Typography variant="body1">
              Mark this invoice as paid and optionally record the payment date.
            </Typography>
            <DatePicker
              label="Invoice Paid on"
              value={markPaidDate}
              onChange={(newValue) => setMarkPaidDate(newValue || dayjs())}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Stack>
        }
        secondaryAction="Cancel"
        secondaryOnClick={closeMarkPaidDialog}
        primaryAction={isMarkPaidSubmitting ? "Saving..." : "Yes, Mark Paid"}
        primaryOnClick={handleConfirmMarkPaid}
      />
      {isPrinting && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRadius: "16px",
              boxShadow: theme.shadows[8],
              minWidth: "320px",
              maxWidth: "420px",
              width: "100%",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <Typography variant="h4" fontWeight={700} textAlign="center">
              Generating PDF…
            </Typography>
            <Typography variant="body1" textAlign="center">
              {printStatus}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={printProgress}
              sx={{ height: 10, borderRadius: 9999 }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Please wait, this may take a few seconds.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default InvoiceProfile;
